import React from "react";
import {
  FiMail,
} from "react-icons/fi";
import Footer from "./pages/Footer/Footer";
import LegalTableOfContents from "./components/LegalTableOfContents";

const navigation = [
  { id: "introduction", label: "Introduction" },
  { id: "website-use", label: "Use of the website" },
  { id: "intellectual-property", label: "Intellectual property" },
  { id: "warranties", label: "Disclaimer of warranties" },
  { id: "liability", label: "Limitation of liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "modifications", label: "Modification of terms" },
  { id: "governing-law", label: "Governing law" },
  { id: "agreement", label: "Entire agreement" },
  { id: "contact", label: "Contact us" },
];

const TermsSection = ({ id, number, title, children }) => (
  <section
    id={id}
    className="scroll-mt-28 border-b border-slate-200 py-9 last:border-b-0 md:py-11"
  >
    <div className="flex items-start gap-4 md:gap-5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFDB16] text-xs font-extrabold text-slate-950 shadow-sm shadow-amber-200">
        {number}
      </span>
      <div className="min-w-0">
        <h2 className="font-heading text-2xl font-bold leading-tight text-slate-950 md:text-[28px]">
          {title}
        </h2>
        <div className="mt-4 space-y-4 text-[16px] leading-7 text-slate-600 md:text-[17px] md:leading-8">
          {children}
        </div>
      </div>
    </div>
  </section>
);

const TermsAndConditions = () => {
  return (
    <>
      <main className="relative bg-slate-100 px-4 pb-12 pt-32 text-slate-900 md:px-[5%] md:pt-40 lg:pb-8 lg:pt-28">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-32 bg-black md:h-40 lg:h-28"
        />
        <div className="relative z-[1] mx-auto w-full overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 lg:overflow-visible">
          <div className="grid lg:items-start lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="relative overflow-hidden bg-[#101827] text-white lg:sticky lg:top-28 lg:self-start lg:rounded-l-[30px]">
              <div className="absolute -left-20 -top-20 h-52 w-52 rounded-full bg-[#643c90]/40 blur-3xl" />
              <div className="absolute -bottom-20 -right-20 h-52 w-52 rounded-full bg-[#FFDB16]/15 blur-3xl" />

              <div className="relative flex flex-col p-6 md:p-8 lg:p-7">
                <LegalTableOfContents
                  items={navigation}
                  ariaLabel="Terms and Conditions table of contents"
                  className="mt-0"
                />

              </div>
            </aside>

            <article className="min-w-0 bg-white lg:overflow-hidden lg:rounded-r-[30px]">
              <header className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-white via-amber-50/70 to-purple-50 px-6 py-9 md:px-12 md:py-11 lg:px-16 xl:px-20">
                <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full border-[28px] border-[#FFDB16]/20" />
                <div className="relative max-w-4xl">
                  <h2 className="max-w-2xl font-heading text-3xl font-extrabold leading-tight text-slate-950 md:text-5xl">
                    Term And Conditions
                  </h2>
                </div>
              </header>

              <div className="mx-auto max-w-6xl px-6 md:px-12 lg:px-16 xl:px-20">
                <TermsSection id="introduction" number="01" title="Introduction">
                  <p>
                    Welcome to the website of Abscod Informatics. These terms
                    and conditions apply to your use of our website. By using
                    our website, you agree to comply with and be bound by these
                    terms and conditions.
                  </p>
                </TermsSection>

                <TermsSection id="website-use" number="02" title="Use of the website">
                  <p>
                    You may use our website for lawful purposes only. You are
                    not permitted to use our website for any unlawful or
                    fraudulent activity or to infringe on the rights of others.
                    You are solely responsible for all content that you post or
                    upload to our website.
                  </p>
                </TermsSection>

                <TermsSection
                  id="intellectual-property"
                  number="03"
                  title="Intellectual property"
                >
                  <p>
                    All content on our website, including but not limited to
                    text, graphics, images, logos, and software, is the property
                    of Abscod Informatics or its licensors and is protected by
                    intellectual property laws. You may not copy, reproduce, or
                    distribute any content on our website without our prior
                    written consent.
                  </p>
                </TermsSection>

                <TermsSection id="warranties" number="04" title="Disclaimer of warranties">
                  <p>
                    Our website is provided on an “as is” and “as available”
                    basis. We do not guarantee that our website will be
                    uninterrupted or error-free. We make no warranties, express
                    or implied, regarding the accuracy, completeness, or
                    reliability of any content on our website.
                  </p>
                </TermsSection>

                <TermsSection id="liability" number="05" title="Limitation of liability">
                  <p>
                    In no event shall Abscod Informatics be liable for any
                    damages, including without limitation, direct or indirect,
                    incidental, consequential, or punitive damages, arising out
                    of or in connection with the use or inability to use our
                    website.
                  </p>
                </TermsSection>

                <TermsSection id="indemnification" number="06" title="Indemnification">
                  <p>
                    You agree to indemnify and hold Abscod Informatics and its
                    officers, directors, employees, and agents harmless from any
                    and all claims, demands, losses, damages, or expenses
                    arising out of or in connection with your use of our
                    website.
                  </p>
                </TermsSection>

                <TermsSection id="modifications" number="07" title="Modification of terms">
                  <p>
                    We reserve the right to modify these terms and conditions
                    at any time without prior notice. Your continued use of our
                    website after any changes have been made constitutes your
                    acceptance of the new terms and conditions.
                  </p>
                </TermsSection>

                <TermsSection id="governing-law" number="08" title="Governing law">
                  <p>
                    These terms and conditions shall be governed by and
                    construed in accordance with the laws of the jurisdiction
                    in which Abscod Informatics is located, without giving
                    effect to any principles of conflicts of law.
                  </p>
                </TermsSection>

                <TermsSection id="agreement" number="09" title="Entire agreement">
                  <p>
                    These terms and conditions constitute the entire agreement
                    between you and Abscod Informatics with respect to your use
                    of our website.
                  </p>
                </TermsSection>

                <TermsSection id="contact" number="10" title="Contact us">
                  <div className="rounded-2xl border border-purple-100 bg-purple-50/70 p-5 md:p-6">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#643c90] text-white">
                        <FiMail />
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800">
                          Have a question about these terms?
                        </p>
                        <p className="mt-1 text-slate-600">
                          Contact our team at{
                          " "
                          }<a
                            href="mailto:info@abscod.com"
                            className="font-bold text-[#643c90] underline decoration-[#643c90]/30 underline-offset-4 hover:text-[#432663]"
                          >
                            info@abscod.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </div>
                </TermsSection>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default TermsAndConditions;
